import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Button,
  Row,
  Col,
  Typography,
  Alert,
  Badge,
  InputNumber,
  Divider,
  Space,
  Tooltip,
  message,
  Form,
  Input,
} from "antd";
import {
  CreditCardOutlined,
  DollarOutlined,
  TeamOutlined,
  TagOutlined,
  CalculatorOutlined,
  InfoCircleOutlined,
  PercentageOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../store/store";
import { fetchSubInfo } from "../../../store/slices/auth.slice";
import {
  productService,
  Product,
} from "../../../services/product/product.service";
import companyService from "../../../services/company/company.service";
import couponService from "../../../services/coupon/coupon.service";
import subService from "../../../services/sub/sub.service";
import { handleApiError } from "../../../utils/errorHandler";
import { useTranslation } from "react-i18next";
import { useCompanyData } from "../../../hooks/useCompanyData";
import BillingAddressForm from "../../shared/components/BillingAddressForm";
import "./pricing.css";

const { Title, Text, Paragraph } = Typography;

const PricingPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { user, sub, subLoading } = useSelector(
    (state: RootState) => state.auth
  );

  const [userCount, setUserCount] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeUsers, setActiveUsers] = useState(0);
  // Fiyatlandırma ürünleri
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [pricingError, setPricingError] = useState<string | null>(null);

  // Billing address
  const [billingAddress, setBillingAddress] = useState<any>(null);
  const { companyData, loading: companyLoading } = useCompanyData();

  // Sub bilgilerini yükle
  useEffect(() => {
    if (user?.companyId && !sub && !subLoading) {
      dispatch(fetchSubInfo(user.companyId));
    }
  }, [dispatch, user?.companyId, sub, subLoading]);

  // Sub bilgileri yüklendiğinde kullanıcı sayısını güncelle
  useEffect(() => {
    if (sub && sub.userLimit) {
      const userLimitNumber = parseInt(sub.userLimit.toString(), 10);
      setUserCount(userLimitNumber);
    }
  }, [sub]);

  // Fiyatlandırma ürünlerini çek - sayfa yenileme sorununu çöz
  useEffect(() => {
    fetchActiveUsers();
  }, []);

  const fetchActiveUsers = async () => {
    if (user?.companyId) {
      const activeUsers = await companyService.getCompanyActiveUsers(
        user?.companyId!
      );
      setActiveUsers(activeUsers.activeUserCount);
    }
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        setPricingError(null);
        const fetchedProducts = await productService.getAllProducts();
        if (fetchedProducts.length === 0) {
          throw new Error("Fiyatlandırma bilgileri alınamadı.");
        }
        setProducts(fetchedProducts);

        // İlk aktif ürünü varsayılan olarak seç - sadece products boşsa
        if (fetchedProducts.length > 0 && !selectedProduct) {
          const defaultProduct = fetchedProducts.find((p) => p.isActive);
          if (defaultProduct) {
            setSelectedProduct(defaultProduct);
          }
        }
      } catch (error) {
        setPricingError(
          "Fiyatlandırma bilgileri yüklenirken bir hata oluştu. Lütfen sayfayı yenileyin."
        );
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, []); // selectedProduct dependency'sini kaldırdım

  // Yeni paket yapısına uygun fiyat hesaplama
  const calculatePricing = useMemo(() => {
    if (products.length === 0 || !selectedProduct) {
      return {
        packageTotal: 0,
        packageTotalWithTax: 0,
        tax: { amount: 0, total: 0 },
        userCount,
        couponDiscount: 0,
        basePrice: 0,
        finalPrice: 0,
        monthlyUserPrice: 0,
        basePackagePrice: 0,
        monthlyEquivalent: 0,
      };
    }

    const monthlyUserPrice = selectedProduct.pricePerUserPerMonth;
    const basePackagePrice =
      userCount * selectedProduct.durationMonths * monthlyUserPrice;

    // Kupon uygulanmışsa
    let finalPackagePrice = basePackagePrice;
    let couponDiscount = 0;

    if (appliedCoupon && appliedCoupon.isValid) {
      if (appliedCoupon.originalAmount === basePackagePrice) {
        finalPackagePrice = appliedCoupon.finalAmount || basePackagePrice;
        couponDiscount = appliedCoupon.savings || 0;
      }
    }

    // Vergi hesaplama
    const taxRate = 0.2;
    const taxAmount = finalPackagePrice * taxRate;
    const finalPackageTotalWithTax = finalPackagePrice + taxAmount;
    const monthlyEquivalent =
      finalPackagePrice / selectedProduct.durationMonths;

    return {
      packageTotal: finalPackagePrice,
      packageTotalWithTax: finalPackageTotalWithTax,
      tax: { amount: taxAmount, total: finalPackageTotalWithTax },
      userCount,
      couponDiscount,
      basePrice: basePackagePrice,
      finalPrice: finalPackagePrice,
      monthlyUserPrice,
      basePackagePrice,
      monthlyEquivalent,
    };
  }, [userCount, selectedProduct, appliedCoupon, products]);

  // Kullanıcı sayısı değişimi - kupon kontrolü ile
  const handleUserCountChange = (value: number | null) => {
    if (value && value > 0) {
      // Kupon uygulanmışsa ve kişi sayısı değiştiriliyorsa uyarı ver
      if (appliedCoupon && appliedCoupon.lockedUserCount !== value) {
        message.warning({
          content:
            "Kişi sayısını değiştirdiğiniz için kupon iptal edildi. Kupon kodunuzu tekrar girebilirsiniz.",
          duration: 4,
        });
        setAppliedCoupon(null);
        setCouponCode("");
      }
      setUserCount(value);
    }
  };

  // Paket seçimi - kupon kontrolü ile
  const handleProductSelection = (product: Product) => {
    // Kupon uygulanmışsa ve paket değiştiriliyorsa uyarı ver
    if (appliedCoupon && appliedCoupon.lockedProductId !== product.id) {
      message.warning({
        content:
          "Paketi değiştirdiğiniz için kupon iptal edildi. Kupon kodunuzu tekrar girebilirsiniz.",
        duration: 4,
      });
      setAppliedCoupon(null);
      setCouponCode("");
    }

    setSelectedProduct(product);
  };

  // Kupon uygulama
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      message.warning("Lütfen bir kupon kodu girin");
      return;
    }

    if (!selectedProduct) {
      message.warning("Lütfen önce bir paket seçin");
      return;
    }

    setCouponLoading(true);

    try {
      // Backend API çağrısı - Base price kullan (kupon öncesi fiyat)
      const currentBasePrice =
        userCount *
        selectedProduct.durationMonths *
        selectedProduct.pricePerUserPerMonth;

      if (!user?.companyId) {
        message.error("Şirket bilgileri yüklenemedi. Lütfen tekrar deneyin.");
        return;
      }

      const response = await couponService.validateCoupon({
        code: couponCode.trim().toUpperCase(),
        orderAmount: currentBasePrice,
        companyId: user?.companyId,
        productId: selectedProduct.id,
        userCount: userCount,
      });

      if (response.isValid) {
        // Kupon geçerli - fiyata uygula
        const appliedCouponData = {
          isValid: true,
          code: couponCode.trim().toUpperCase(),
          originalAmount: currentBasePrice,
          finalAmount: response.finalAmount || currentBasePrice,
          savings: response.discountAmount || 0,
          message: response.message,
          couponType: response.couponType,
          couponValue: response.couponValue,
          // Kupon kısıtlamaları
          lockedProductId: selectedProduct.id,
          lockedUserCount: userCount,
        };

        setAppliedCoupon(appliedCouponData);

        // Kupon tipine göre mesaj oluştur
        const discountText =
          response.couponType === "percentage"
            ? `%${response.couponValue} indirim`
            : `₺${response.couponValue} indirim`;

        message.success(
          `${response.message} - ${discountText} uygulandı! ₺${(
            response.discountAmount || 0
          ).toFixed(2)} tasarruf!`
        );
      } else {
        // Kupon geçersiz
        message.error(response.message || "Geçersiz kupon kodu");
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Kupon doğrulanırken bir hata oluştu. Lütfen tekrar deneyin.";
      message.error(errorMessage);
    } finally {
      setCouponLoading(false);
    }
  };

  // Kupon kaldırma
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    message.info("Kupon indirimi kaldırıldı");
  };

  // Plan satın alma - IYZICO entegrasyonu
  const handlePurchase = async () => {
    if (!selectedProduct) {
      message.warning("Lütfen bir paket seçin");
      return;
    }

    if (!user?.companyId) {
      message.error("Şirket bilgileri yüklenemedi. Lütfen tekrar deneyin.");
      return;
    }

    try {
      setLoading(true);


      localStorage.removeItem("checkoutsession");
      localStorage.removeItem("conversationId");
      localStorage.removeItem("sms_checkoutsession");
      localStorage.removeItem("sms_conversationId");

      // IYZICO entegrasyonuna uygun request body
      const purchaseRequest = {
        companyId: user.companyId,
        userLimit: userCount,
        duration: selectedProduct.durationMonths,
        finalAmount: calculatePricing.packageTotalWithTax, // KDV dahil fiyat gönderiliyor
        billingAddress: billingAddress || {
          alias: "Varsayılan Adres",
          contactName: user.firstName + " " + user.lastName,
          address: companyData?.address || "Varsayılan Adres",
          city: companyData?.city || "Ankara",
          district: companyData?.district || "Etimesgut",
          country: "Turkey",
          zipCode: "07000",
          phone: companyData?.phone || user.phone || "",
          email: companyData?.email || user.email || "",
        },
        couponCode: appliedCoupon?.code || undefined,
        isInternalRequest: true,
      };

      // console.log('🚀 Purchase request:', purchaseRequest);

      // Backend'e purchase request gönder
      const result = await subService.purchasePackage(purchaseRequest);

      // console.log('📥 Purchase response:', result);

      if (result.checkoutForm && result.checkoutForm.checkoutFormContent) {
        // Token'ları localStorage'a kaydet (callback için)
        localStorage.setItem("checkoutsession", result.checkoutForm.token);
        localStorage.setItem(
          "conversationId",
          result.checkoutForm.conversationId
        );

        // Yeni pencerede checkout form'u aç
        const newWindow = window.open("", "_blank");
        if (newWindow) {
          newWindow.document.write(result.checkoutForm.checkoutFormContent);
          newWindow.document.close();
          message.success(
            "💳 Ödeme Sayfası Açıldı! Ödeme işleminizi yeni sekmede tamamlayın."
          );
        } else {
          message.error(
            "Popup Engellendi. Ödeme sayfasını açmak için lütfen tarayıcınızda popup'lara izin verin ve tekrar deneyin."
          );
        }
      } else {
        message.error("Ödeme formu alınamadı. Lütfen tekrar deneyin.");
      }
    } catch (error: any) {
      handleApiError(error, t);
      message.error("Ödeme işlemi başlatılırken bir hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  // Hero icon'u subscription status'a göre belirle
  const getHeroIcon = () => {
    return (
      <ExclamationCircleOutlined
        className="hero-icon"
        style={{ color: "#faad14" }}
      />
    );
  };

  if (
    subLoading ||
    (!sub && user?.companyId) ||
    productsLoading ||
    companyLoading
  ) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <Card loading />
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper pricing-page">
      <div className="content container-fluid">
        {/* Header */}
        <div className="pricing-header text-center mb-5">
          <div className="pricing-hero">
            {getHeroIcon()}

            <Title level={2} className="hero-title">
              {sub?.status === "trial"
                ? "Deneme Süreniz Sona Erdi"
                : sub?.status === "expired" || sub?.status === "inactive"
                ? "Aboneliğiniz Sona Erdi"
                : "Premium Plana Geçin"}
            </Title>
            <Paragraph
              className="hero-description"
              style={{ fontSize: "18px", maxWidth: "600px", margin: "0 auto" }}
            >
              {sub?.status === "trial"
                ? "Deneme süreniz sona erdi. Dinox'un tüm özelliklerinden yararlanmaya devam etmek için premium planımıza geçin. Esnek paket seçenekleri ve özel indirimlerle hemen başlayın!"
                : "Dinox'un tüm özelliklerinden yararlanmaya devam etmek için premium paketlerimizden birini seçin. Esnek paket seçenekleri ve özel indirimlerle hemen başlayın!"}
            </Paragraph>
          </div>

          {pricingError && (
            <Alert
              message="Fiyatlandırma Hatası"
              description={pricingError}
              type="error"
              showIcon
              className="my-4"
            />
          )}

          {sub && (
            <Alert
              message={
                <Space>
                  <ClockCircleOutlined />
                  <Text strong>
                    {sub.status === "trial"
                      ? "Deneme Süresi Durumu"
                      : sub.status === "expired" || sub.status === "inactive"
                      ? "Abonelik Süresi Sona Erdi"
                      : "Abonelik Durumu"}
                  </Text>
                </Space>
              }
              description={
                <div>
                  <Text>
                    {sub.status === "trial"
                      ? `Deneme süreniz ${sub.daysLeft} gün sonra sona erecek`
                      : sub.status === "expired" || sub.status === "inactive"
                      ? "Aboneliğiniz sona erdi. Yeni paket satın alarak hizmetinize devam edebilirsiniz."
                      : "Abonelik aktif"}
                  </Text>
                  <br />
                  <Text type="secondary">
                    {sub.status === "trial"
                      ? `Deneme kullanıcı limiti: ${sub.userLimit} kullanıcı`
                      : `Mevcut plan limiti: ${sub.userLimit} kullanıcı`}
                  </Text>
                </div>
              }
              type={
                sub.status === "expired" || sub.status === "inactive"
                  ? "error"
                  : "warning"
              }
              showIcon
              className="trial-status-alert"
            />
          )}
        </div>

        {/* Ana İçerik Kartı */}
        <Card className="pricing-main-card" style={{ padding: "24px" }}>
          <Title level={4} className="mb-4">
            <CreditCardOutlined className="me-2" />
            Yeni Abonelik Planı
          </Title>

          {/* Kullanıcı Sayısı Hesaplayıcı */}
          <Card
            className="mb-4"
            size="small"
            style={{ padding: "16px", border: "1px solid #e2e8f0" }}
          >
            <Row gutter={[24, 16]} align="middle">
              <Col xs={24}>
                <div className="calculator-section">
                  <Title level={5} className="calculator-title">
                    <TeamOutlined className="me-2" />
                    Kaç kullanıcınız olacak?
                  </Title>
                  <InputNumber
                    min={1}
                    max={500000}
                    value={userCount}
                    onChange={handleUserCountChange}
                    size="large"
                    style={{ width: "100%" }}
                    parser={(value: any) => value!.replace(/\$\s?|(,*)/g, "")}
                    addonAfter="kullanıcı"
                  />
                  <Text type="secondary" className="pricing-info mt-2">
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      Minimum: {activeUsers} kullanıcı (mevcut aktif kullanıcı
                      sayısı)
                    </Text>
                    {appliedCoupon && (
                      <Text
                        type="success"
                        style={{
                          fontSize: "12px",
                          display: "block",
                          marginTop: "4px",
                        }}
                      >
                        ✨ Kişi sayısı değiştirilirse "{appliedCoupon.code}"
                        kupon kodunuz devre dışı olacak. Kuponu tekrar
                        kullanabilirsiniz.
                      </Text>
                    )}
                  </Text>
                </div>
              </Col>

              {/* <Col xs={24} lg={12}>
                <div className="price-summary">
                  <div className="price-card" style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    padding: '24px',
                    borderRadius: '16px',
                    border: 'none',
                    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.15)',
                    textAlign: 'center'
                  }}>
                    <Statistic
                      title={<span style={{ color: '#ffffff', fontSize: '16px', fontWeight: 600 }}>Aylık Eşdeğer Ücret</span>}
                      value={calculatePricing.monthlyEquivalent}
                      precision={2}
                      suffix="₺"
                      valueStyle={{ color: '#ffffff', fontSize: '32px', fontWeight: 'bold' }}
                    />
                    <div className="price-breakdown" style={{ marginTop: '16px' }}>
                      <Text style={{ color: '#e2e8f0', fontSize: '14px' }}>
                        {selectedProduct ? `${selectedProduct.durationMonths} aylık paket` : 'Paket seçin'}
                      </Text>
                      <br />
                      <Text style={{ color: '#68d391', fontSize: '14px', fontWeight: 600, marginTop: '8px', display: 'block' }}>
                        <GiftOutlined className="me-1" />
                        Paket avantajları
                      </Text>
                    </div>
                  </div>
                </div>
              </Col> */}
            </Row>
          </Card>

          {/* Kupon Kodu Bölümü */}

          {/* Paket Seçimi */}
          <Card
            className="payment-method-section mb-4"
            size="small"
            style={{ padding: "16px" }}
          >
            <Title level={5} className="mb-3">
              <DollarOutlined className="me-2" />
              Paket Süresini Seçin
              {appliedCoupon && (
                <Text
                  type="success"
                  style={{
                    fontSize: "12px",
                    display: "block",
                    marginTop: "4px",
                    fontWeight: "normal",
                  }}
                >
                  ✨ Paket değiştirilirse "{appliedCoupon.code}" kupon kodunuz
                  devre dışı olacak. Kuponu tekrar kullanabilirsiniz.
                </Text>
              )}
            </Title>

            <Row gutter={[16, 16]}>
              {products
                .filter((p) => p.isActive)
                .map((product) => (
                  <Col xs={24} sm={12} lg={8} key={product.id}>
                    <div
                      className={`package-selection-card ${
                        selectedProduct?.id === product.id ? "selected" : ""
                      }`}
                      onClick={() => handleProductSelection(product)}
                      style={{
                        border:
                          selectedProduct?.id === product.id
                            ? "2px solidrgb(205, 73, 246)"
                            : "1px solid #e2e8f0",
                        borderRadius: "12px",
                        padding: "20px",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        background:
                          selectedProduct?.id === product.id
                            ? "rgb(245, 240, 254)"
                            : "white",
                        position: "relative",
                        height: "100%",
                      }}
                    >
                      {/* İndirim yüzdesi sticker'ı - 3 aylık paket dışında */}
                      {product.durationMonths !== 3 &&
                        (() => {
                          const threeMonthProduct = products.find(
                            (p) => p.durationMonths === 3
                          );
                          if (threeMonthProduct) {
                            const threeMonthPrice = Number(
                              threeMonthProduct.pricePerUserPerMonth
                            );
                            const currentPrice = Number(
                              product.pricePerUserPerMonth
                            );
                            const discountPercentage = Math.round(
                              ((threeMonthPrice - currentPrice) /
                                threeMonthPrice) *
                                100
                            );

                            if (discountPercentage > 0) {
                              return (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "-24px",
                                    right: "-12px",
                                    background:
                                      "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
                                    color: "white",
                                    borderRadius: "50%",
                                    width: "50px",
                                    height: "50px",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "10px",
                                    fontWeight: "bold",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                    zIndex: 10,
                                    textAlign: "center",
                                    lineHeight: "1",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "18px",
                                      marginBottom: "1px",
                                    }}
                                  >
                                    %{discountPercentage}
                                  </div>
                                  <div style={{ fontSize: "10px" }}>
                                    İndirim
                                  </div>
                                </div>
                              );
                            }
                          }
                          return null;
                        })()}

                      {selectedProduct?.id === product.id && (
                        <div
                          style={{
                            position: "absolute",
                            top: "12px", // İndirim sticker'ı varsa aşağıya kaydır
                            left: "12px",
                            background: "rgb(181, 44, 223)",
                            color: "white",
                            borderRadius: "50%",
                            width: "20px",
                            height: "20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            zIndex: 11,
                          }}
                        >
                          ✓
                        </div>
                      )}

                      <div style={{ textAlign: "center" }}>
                        <Title
                          level={5}
                          style={{ color: "#2d3748", marginBottom: "8px" }}
                        >
                          {product.name}
                        </Title>
                        {/* <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
                        {product.durationMonths} Aylık Paket
                      </Text> */}

                        <div style={{ marginBottom: "12px" }}>
                          {/* 3 aylık paket dışında üzeri çizili fiyat göster */}
                          {product.durationMonths !== 3 && (
                            <div style={{ marginBottom: "4px" }}>
                              <Text
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "normal",
                                  color: "#8c8c8c",
                                  textDecoration: "line-through",
                                }}
                              >
                                ₺
                                {Number(
                                  products.find((p) => p.durationMonths === 3)
                                    ?.pricePerUserPerMonth
                                )}
                              </Text>
                              <br />
                            </div>
                          )}

                          {/* Mevcut fiyat */}
                          <div>
                            <Text
                              style={{
                                fontSize: "24px",
                                fontWeight: "bold",
                                color: "rgb(181, 44, 223)",
                              }}
                            >
                              ₺{product.pricePerUserPerMonth}
                            </Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              /kullanıcı/ay
                            </Text>
                          </div>
                        </div>

                        {product.description && (
                          <Text
                            type="secondary"
                            style={{
                              fontSize: "12px",
                              display: "block",
                              marginBottom: "8px",
                            }}
                          >
                            {product.description}
                          </Text>
                        )}

                        {/* <div style={{ marginTop: '12px', padding: '8px', background: '#fff7e6', borderRadius: '8px' }}>
                        <Text style={{ fontSize: '12px', color: '#faad14', fontWeight: 600 }}>
                          Toplam: ₺{Math.round(newUserCount * product.durationMonths * product.pricePerUserPerMonth)}
                        </Text>
                        <br />
                        <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
                          ({newUserCount} kullanıcı × {product.durationMonths} ay)
                        </Text>
                      </div> */}
                      </div>
                    </div>
                  </Col>
                ))}
            </Row>
          </Card>

          <Card
            className="coupon-section-card"
            style={{
              border: "1px solid #e2e8f0",
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <Title level={5} className="mb-3">
              <TagOutlined className="me-2" />
              Kupon Kodunuz Var mı?
            </Title>

            <Row gutter={[16, 16]} align="middle">
              <Col xs={24}>
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    placeholder="Kupon kodunu girin"
                    value={couponCode}
                    onChange={(e) =>
                      setCouponCode(e.target.value.toUpperCase())
                    }
                    size="large"
                    disabled={!!appliedCoupon || !selectedProduct}
                    style={{
                      borderRadius: "12px 0 0 12px",
                      border: "2px solid #e2e8f0",
                      fontSize: "16px",
                      height: "48px",
                    }}
                  />
                  <Button
                    type="primary"
                    onClick={handleApplyCoupon}
                    loading={couponLoading}
                    disabled={!!appliedCoupon || !selectedProduct}
                    size="large"
                    style={{
                      height: "48px",
                      borderRadius: "0 12px 12px 0",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      border: "none",
                      fontWeight: 600,
                    }}
                  >
                    Uygula
                  </Button>
                </Space.Compact>
              </Col>
            </Row>

            {!selectedProduct && (
              <Text
                type="secondary"
                style={{ fontSize: "12px", display: "block", marginTop: "8px" }}
              >
                Kupon kodu kullanmak için önce bir paket seçin
              </Text>
            )}

            {appliedCoupon && (
              <Alert
                message={
                  <Space>
                    <PercentageOutlined />
                    <span>
                      Kupon Uygulandı: <strong>{appliedCoupon.code}</strong>
                    </span>
                    {appliedCoupon.couponType && (
                      <Badge
                        color={
                          appliedCoupon.couponType === "percentage"
                            ? "green"
                            : "blue"
                        }
                        text={
                          appliedCoupon.couponType === "percentage"
                            ? `%${appliedCoupon.couponValue} İndirim`
                            : `₺${appliedCoupon.couponValue} İndirim`
                        }
                      />
                    )}
                  </Space>
                }
                description={
                  <div>
                    <Text>{appliedCoupon.message}</Text>
                    <br />
                    <Text style={{ color: "#11998e", fontWeight: 600 }}>
                      <strong>
                        Tasarruf: ₺{appliedCoupon.savings.toFixed(2)}
                      </strong>
                    </Text>
                  </div>
                }
                type="success"
                showIcon
                closable
                onClose={handleRemoveCoupon}
                style={{
                  marginTop: "16px",
                  borderRadius: "12px",
                  border: "2px solid #11998e",
                }}
              />
            )}
          </Card>

          {/* Seçilen Paket Bilgileri */}
          {/* {selectedProduct && (() => {
            const pricePerUser = Number(selectedProduct.pricePerUserPerMonth);
            
            const monthlyBasisPrice = userCount * pricePerUser;
            const totalMonthlyEquivalent = monthlyBasisPrice * selectedProduct.durationMonths;
            
            const packagePrice = userCount * selectedProduct.durationMonths * pricePerUser;
            
            const savings = totalMonthlyEquivalent - packagePrice;
            const discountPercentage = savings > 0 ? ((savings / totalMonthlyEquivalent) * 100) : 0;
            
            return (
              <Card style={{ padding: '16px', marginBottom: '16px', border: '2px solid #52c41a', background: '#f6ffed' }}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <div className="selected-package-info">
                      <Title level={5} style={{ color: '#52c41a', marginBottom: 8 }}>
                        <TrophyOutlined className="me-2" />
                        Seçilen Paket: {selectedProduct.durationMonths} Aylık Plan
                      </Title>
                      <div className="package-details">
                        <Text type="secondary">
                          <strong>{userCount} kullanıcı</strong> × <strong>{selectedProduct.durationMonths} ay</strong> × <strong>₺{pricePerUser.toFixed(2)}/kullanıcı</strong>
                        </Text>
                        <br />
                        <Text style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                          Paket Fiyatı: ₺{packagePrice.toFixed(2)}
                        </Text>
                        {savings > 0 && (
                          <>
                            <br />
                            <Text type="secondary" style={{ textDecoration: 'line-through' }}>
                              Aylık ödemelere göre toplam: ₺{totalMonthlyEquivalent.toFixed(2)}
                            </Text>
                          </>
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} md={12}>
                    <div className="package-benefits">
                      <Title level={5} style={{ color: '#faad14', marginBottom: 8 }}>
                        <GiftOutlined className="me-2" />
                        Paket Avantajları
                      </Title>
                      <div className="benefits-list">
                        {savings > 0 && (
                          <Text type="success" style={{ display: 'block', marginBottom: 4, fontSize: '14px', fontWeight: 'bold' }}>
                            <CheckCircleOutlined className="me-1" />
                            %{discountPercentage.toFixed(1)} indirim - ₺{savings.toFixed(2)} tasarruf
                          </Text>
                        )}
                        <Text type="success" style={{ display: 'block', marginBottom: 4 }}>
                          <CheckCircleOutlined className="me-1" />
                          {savings > 0 
                            ? `Aylık ödemelere göre %${discountPercentage.toFixed(1)} indirimli tek ödeme`
                            : `Aylık ₺${monthlyBasisPrice.toFixed(2)} yerine tek ödeme`
                          }
                        </Text>
                        <Text type="success" style={{ display: 'block', marginBottom: 4 }}>
                          <CheckCircleOutlined className="me-1" />
                          {selectedProduct.durationMonths} ay kesintisiz hizmet garantisi
                        </Text>
                        <Text type="success" style={{ display: 'block', marginBottom: 4 }}>
                          <CheckCircleOutlined className="me-1" />
                          Ödeme güvenliği ve kolaylığı
                        </Text>
                        {selectedProduct.durationMonths >= 12 && (
                          <Text type="success" style={{ display: 'block' }}>
                            <CheckCircleOutlined className="me-1" />
                            Uzun vadeli plan avantajı
                          </Text>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card>
            );
          })()} */}

          {/* Billing Address Form */}
          {selectedProduct && (
            <BillingAddressForm
              form={null}
              companyData={companyData}
              onBillingAddressChange={setBillingAddress}
            />
          )}

          {/* Fiyat Özeti */}
          {selectedProduct && (
            <Card
              className="price-breakdown-card mb-4"
              style={{ padding: "16px" }}
            >
              <Title level={5} className="mb-3">
                <CalculatorOutlined className="me-2" />
                Fiyat Detayları
              </Title>

              <div className="price-breakdown-details">
                <Row justify="space-between" className="price-line">
                  <Col>
                    <Text>
                      Paket Ücreti ({userCount} kullanıcı ×{" "}
                      {selectedProduct.durationMonths} ay)
                    </Text>
                  </Col>
                  <Col>
                    <Text>₺{calculatePricing.basePrice.toFixed(2)}</Text>
                  </Col>
                </Row>

                {appliedCoupon && (
                  <Row justify="space-between" className="price-line discount">
                    <Col>
                      <Text type="success">
                        Kupon İndirimi ({appliedCoupon.code})
                      </Text>
                    </Col>
                    <Col>
                      <Text type="success">
                        -₺{appliedCoupon.savings.toFixed(2)}
                      </Text>
                    </Col>
                  </Row>
                )}

                <Divider style={{ margin: "12px 0" }} />

                <Row justify="space-between" className="price-line subtotal">
                  <Col>
                    <Text strong>Ara Toplam</Text>
                  </Col>
                  <Col>
                    <Text strong>
                      ₺{calculatePricing.finalPrice.toFixed(2)}
                    </Text>
                  </Col>
                </Row>

                <Row justify="space-between" className="price-line tax">
                  <Col>
                    <Text>
                      KDV (%{(0.2 * 100).toFixed(0)})
                      <Tooltip title="Katma Değer Vergisi yasal olarak eklenir">
                        <InfoCircleOutlined className="ms-1" />
                      </Tooltip>
                    </Text>
                  </Col>
                  <Col>
                    <Text>₺{calculatePricing.tax.amount.toFixed(2)}</Text>
                  </Col>
                </Row>

                <Divider style={{ margin: "12px 0" }} />

                <Row justify="space-between" className="price-line total">
                  <Col>
                    <Title level={5} style={{ margin: 0 }}>
                      Toplam Tutar
                    </Title>
                  </Col>
                  <Col>
                    <Title level={4} style={{ margin: 0, color: "#1890ff" }}>
                      ₺{calculatePricing.packageTotalWithTax.toFixed(2)}
                      <Text type="secondary" className="ms-1">
                        ({selectedProduct.durationMonths} ay)
                      </Text>
                    </Title>
                  </Col>
                </Row>
              </div>
            </Card>
          )}

          {/* Satın Alma Butonu */}
          <div className="purchase-section">
            <Alert
              message="Güvenli Ödeme Garantisi"
              description="Tüm ödeme bilgileriniz 256-bit SSL şifreleme ile korunur. Güvenli IYZICO ödeme sayfasına yönlendirileceksiniz."
              type="info"
              showIcon
              className="mb-4"
            />

            <div className="text-center">
              <Button
                type="primary"
                size="large"
                icon={<CreditCardOutlined />}
                loading={loading}
                onClick={handlePurchase}
                disabled={!selectedProduct || !billingAddress}
                style={{
                  height: "60px",
                  fontSize: "18px",
                  minWidth: "300px",
                  background:
                    selectedProduct && billingAddress
                      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      : "#d9d9d9",
                  border: "none",
                }}
              >
                {loading
                  ? "Ödeme Sayfası Hazırlanıyor..."
                  : selectedProduct && billingAddress
                  ? `₺${calculatePricing.packageTotalWithTax.toFixed(
                      2
                    )} - Güvenli Ödeme Sayfasına Git`
                  : !selectedProduct
                  ? "Lütfen Paket Seçin"
                  : "Lütfen Fatura Adresi Belirleyin"}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PricingPage;
